import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useColorPalette } from '../hooks/useColorPalette';

/**
 * Componente MySelect personalizzato che emula il comportamento di Select2
 * ma utilizza react-select che è più compatibile con React
 */
const MySelect = ({
  id,
  placeholder = 'Seleziona...',
  value = null,
  options = [],
  onChange = () => {},
  multiple = false,
  isDisabled = false,
  isClearable = true,
  className = ''
}) => {
  const colors = useColorPalette();
  
  // Formatta il valore per react-select
  const formatValue = () => {
    if (!value) return multiple ? [] : null;
    
    if (multiple && Array.isArray(value)) {
      return value.map(v => ({
        value: v.id || v,
        label: v.text || v.toString()
      }));
    } else {
      // Se value è una stringa semplice, cerca l'opzione corrispondente
      if (typeof value === 'string') {
        const matchingOption = options.find(opt => (opt.id || opt.value) === value);
        if (matchingOption) {
          return {
            value: matchingOption.id || matchingOption.value || value,
            label: matchingOption.text || matchingOption.label || value
          };
        }
        return {
          value: value,
          label: value
        };
      }
      
      return {
        value: value.id || value,
        label: value.text || value.toString()
      };
    }
  };
  
  // Formatta le opzioni per react-select
  const formatOptions = () => {
    return options.map(option => ({
      value: option.id || option.value || option,
      label: option.text || option.label || option.toString()
    }));
  };

  // Stili personalizzati
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: `1px solid ${colors.primary || '#ced4da'}`,
      boxShadow: state.isFocused ? `0 0 0 2px ${colors.primary}30` : 'none',
      '&:hover': {
        border: `1px solid ${colors.primary || '#ced4da'}`
      },
      borderRadius: '4px',
      minHeight: '38px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? colors.primary : state.isFocused ? `${colors.primary}20` : 'white',
      color: state.isSelected ? 'white' : colors.textOnBackground || '#495057'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: `${colors.primary}20`
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: colors.primary
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: colors.primary,
      '&:hover': {
        backgroundColor: colors.primary,
        color: 'white',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d'
    })
  };

  // Gestisce il cambio di valore
  const handleChange = (selected) => {
    if (!selected && !multiple) {
      onChange(null);
      return;
    }
    
    if (multiple) {
      const values = selected ? selected.map(item => ({ id: item.value, text: item.label })) : [];
      onChange(values);
    } else {
      onChange({ id: selected.value, text: selected.label });
    }
  };

  return (
    <Select
      id={id}
      placeholder={placeholder}
      value={formatValue()}
      options={formatOptions()}
      onChange={handleChange}
      isMulti={multiple}
      isDisabled={isDisabled}
      isClearable={isClearable}
      className={`react-select ${className}`}
      classNamePrefix="react-select"
      styles={customStyles}
    />
  );
};

MySelect.propTypes = {
  id: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.any,
  options: PropTypes.array,
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  className: PropTypes.string
};

export default MySelect;
